import { useEffect, useState } from 'react';
import { User, Mail, Crown, LogOut, Calendar, ExternalLink, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import styles from './ProfilePage.module.css';

interface ProfileData {
  subscription_status: string;
  subscription_end_date: number | null;
}

function getSubscriptionLabel(status: string): string {
  switch (status) {
    case 'active': return 'Premium Active';
    case 'trialing': return 'Premium Trial';
    case 'past_due': return 'Payment Past Due';
    case 'canceled': return 'Subscription Canceled';
    case 'free': return 'Free Plan';
    default: return 'Free Plan';
  }
}

function formatUnixDate(unixTimestamp: number | null): string {
  if (!unixTimestamp) return '';
  const date = new Date(unixTimestamp * 1000);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
}

export default function ProfilePage() {
  const { user, session, signOut } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deletionError, setDeletionError] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.email) return;

      try {
        // Step 1: Try RPC function with SECURITY DEFINER (same as mobile app)
        const { data: rpcData, error: rpcError } = await supabase.rpc('find_profile_by_email', {
          p_email: user.email
        });

        if (rpcData && !rpcError) {
          setProfileData(rpcData);
          return;
        }

        // Step 2: Fall back to direct query by email
        const { data: emailData, error: emailError } = await supabase
          .from('profiles')
          .select('subscription_status, subscription_end_date')
          .eq('email', user.email)
          .maybeSingle();

        if (emailData && !emailError) {
          setProfileData(emailData);
          return;
        }

        // Step 3: Fall back to direct query by user_id
        if (user.id) {
          const { data: idData, error: idError } = await supabase
            .from('profiles')
            .select('subscription_status, subscription_end_date')
            .eq('user_id', user.id)
            .maybeSingle();

          if (idData && !idError) {
            setProfileData(idData);
          }
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
      }
    };

    fetchProfile();
  }, [user?.email, user?.id]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE' || !session?.access_token || isDeletingAccount) return;
    setIsDeletingAccount(true);
    setDeletionError('');

    try {
      const response = await fetch('/api/account', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || 'We could not complete deletion. Please contact privacy support.');
      }

      await signOut();
      navigate('/privacy?deletion=complete', { replace: true });
    } catch (error) {
      setDeletionError(error instanceof Error ? error.message : 'We could not complete deletion. Please contact privacy support.');
      setIsDeletingAccount(false);
    }
  };

  const subscriptionStatus = profileData?.subscription_status || 'free';
  const isPremium = subscriptionStatus === 'active' || subscriptionStatus === 'trialing' || subscriptionStatus === 'past_due';
  const subscriptionEndDate = profileData?.subscription_end_date;

  const getEndDateLabel = () => {
    if (!subscriptionEndDate) return null;
    if (subscriptionStatus === 'trialing') {
      return `Trial ends ${formatUnixDate(subscriptionEndDate)}`;
    }
    if (subscriptionStatus === 'active') {
      return `Renews ${formatUnixDate(subscriptionEndDate)}`;
    }
    if (subscriptionStatus === 'past_due') {
      return `Payment due since ${formatUnixDate(subscriptionEndDate)}`;
    }
    if (subscriptionStatus === 'canceled') {
      return `Ended ${formatUnixDate(subscriptionEndDate)}`;
    }
    return null;
  };

  const STRIPE_PORTAL_URL = 'https://peapilhealth.com/manage';

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>
          <User size={28} />
          Profile & Account
        </h1>
      </header>

      <div className={styles.content}>
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Account Information</h2>

          <div className={styles.infoRow}>
            <Mail size={20} />
            <div>
              <span className={styles.infoLabel}>Email</span>
              <span className={styles.infoValue}>{user?.email || 'Not available'}</span>
            </div>
          </div>
        </section>

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Subscription</h2>

          <div className={styles.subscriptionBadge} data-premium={isPremium} data-past-due={subscriptionStatus === 'past_due'}>
            <Crown size={24} />
            <div>
              <span className={styles.subscriptionStatus}>{getSubscriptionLabel(subscriptionStatus)}</span>
              {subscriptionStatus === 'past_due' && <span className={styles.subscriptionWarning}>Please update your payment method</span>}
              {(subscriptionStatus === 'active' || subscriptionStatus === 'trialing') && <span className={styles.subscriptionInfo}>Full access to all features</span>}
            </div>
          </div>

          {getEndDateLabel() && (
            <div className={styles.endDateRow}>
              <Calendar size={18} />
              <span>{getEndDateLabel()}</span>
            </div>
          )}

          {isPremium ? (
            <a 
              href={STRIPE_PORTAL_URL} 
              target="_blank" 
              rel="noopener noreferrer" 
              className={styles.manageButton}
            >
              <ExternalLink size={18} />
              Manage My Subscription
            </a>
          ) : (
            <a 
              href="https://peapilpublishing.com/pages/peapil-health" 
              target="_blank" 
              rel="noopener noreferrer" 
              className={styles.manageButton}
            >
              <ExternalLink size={18} />
              Explore Subscriptions
            </a>
          )}
        </section>

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Session</h2>
          <p className={styles.sessionInfo}>
            You are currently signed in.
          </p>
          <button onClick={handleSignOut} className={styles.signOutButton}>
            <LogOut size={20} />
            Sign Out
          </button>
        </section>

        <section className={`${styles.card} ${styles.dangerCard}`}>
          <h2 className={styles.cardTitle}>Delete account and data</h2>
          <p className={styles.deletionWarning}>
            Deleting your account permanently removes your Peapil sign-in, profile, health profile, meal plans,
            shopping lists, and other linked account records. This cannot be undone. Some records may be retained
            where legally required or in protected backups for a limited period.
          </p>
          <p className={styles.deletionWarning}>
            This action does not remove mobile-app food logs or uploaded meal-scan photos. To request deletion
            of those records, email privacy support using the address linked to your account.
          </p>
          <p className={styles.deletionWarning}>
            This does not cancel a subscription or delete records held by external providers. Cancel any
            subscription separately before continuing.
          </p>
          {!showDeleteConfirmation ? (
            <button
              type="button"
              onClick={() => {
                setShowDeleteConfirmation(true);
                setDeletionError('');
              }}
              className={styles.deleteAccountButton}
            >
              <Trash2 size={18} />
              Request account deletion
            </button>
          ) : (
            <div className={styles.deleteConfirmation}>
              <label htmlFor="delete-account-confirmation">
                Type <strong>DELETE</strong> to confirm permanent deletion.
              </label>
              <input
                id="delete-account-confirmation"
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                autoComplete="off"
                disabled={isDeletingAccount}
              />
              {deletionError && <p className={styles.deletionError} role="alert">{deletionError}</p>}
              <div className={styles.deleteActions}>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    setDeleteConfirmation('');
                    setDeletionError('');
                  }}
                  className={styles.cancelDeleteButton}
                  disabled={isDeletingAccount}
                >
                  Keep my account
                </button>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  className={styles.deleteAccountButton}
                  disabled={deleteConfirmation !== 'DELETE' || isDeletingAccount}
                >
                  <Trash2 size={18} />
                  {isDeletingAccount ? 'Deleting account…' : 'Delete my account'}
                </button>
              </div>
            </div>
          )}
          <p className={styles.externalDeletion}>
            Can’t sign in? <a href="mailto:support@peapil.com?subject=Account%20and%20data%20deletion%20request">Email privacy support to request deletion</a>.
          </p>
        </section>

      </div>

      <footer className={styles.footer}>
        <p>Peapil Health Web v5.0.2</p>
        <p>For questions or support, please contact <a href="mailto:support@peapilhealth.com" className={styles.emailLink}>support@peapilhealth.com</a></p>
      </footer>
    </div>
  );
}
